import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast, Toaster } from 'sonner';
import { CheckCircle, XCircle, MessageCircle, Phone, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PatientEstimateView() {
  const { estimateId } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [responseNotes, setResponseNotes] = useState('');
  const [responding, setResponding] = useState(false);
  const [responded, setResponded] = useState(false);

  useEffect(() => {
    const fetchEstimate = async () => {
      try {
        const res = await fetch(`${API_URL}/api/dental-chart/estimates/${estimateId}`);
        if (res.ok) {
          const data = await res.json();
          setEstimate(data);
          if (['accepted', 'declined', 'modification_requested'].includes(data.status)) {
            setResponded(true);
          }
        }
      } catch (err) {
        console.error('Failed to fetch estimate:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEstimate();
  }, [estimateId]);

  const respond = async (responseType) => {
    setResponding(true);
    try {
      const res = await fetch(`${API_URL}/api/dental-chart/estimates/${estimateId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ response: responseType, notes: responseNotes })
      });
      if (res.ok) {
        toast.success(responseType === 'accepted' ? 'Estimate accepted!' : responseType === 'declined' ? 'Estimate declined' : 'Modification request sent');
        setResponded(true);
        setEstimate(prev => ({ ...prev, status: responseType }));
      }
    } catch {
      toast.error('Failed to send response');
    } finally {
      setResponding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-10 h-10 border-4 border-[#1a3c5e] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-lg font-semibold text-slate-700">Estimate not found</p>
          <Link to="/" className="text-sm text-blue-600 hover:underline mt-2 block">Go to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="patient-estimate-page">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-[#1a3c5e] text-white py-4 px-4">
        <div className="max-w-2xl mx-auto flex items-center gap-3">
          <Link to="/" className="p-1 hover:bg-white/10 rounded">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="font-semibold">Your Dental Estimate</h1>
            <p className="text-xs text-white/70">MED CONNECT ALBANIA</p>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Patient Info */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{estimate.patient_name}</p>
                <p className="text-sm text-muted-foreground">{estimate.patient_email}</p>
              </div>
              <Badge className={
                estimate.status === 'accepted' ? 'bg-green-100 text-green-700' :
                estimate.status === 'declined' ? 'bg-red-100 text-red-700' :
                estimate.status === 'modification_requested' ? 'bg-amber-100 text-amber-700' :
                'bg-blue-100 text-blue-700'
              }>
                {estimate.status?.replace(/_/g, ' ')}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Treatment Details */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Treatment Plan</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {estimate.teeth?.map((tooth, idx) => (
              <div key={idx} className="bg-slate-50 rounded-lg p-3">
                <p className="font-medium text-sm mb-1.5">Tooth #{tooth.tooth_number}</p>
                {tooth.interventions?.map((iv, i) => (
                  <div key={i} className="flex items-center justify-between text-sm py-0.5">
                    <span className="text-slate-600">{iv.treatment_name}</span>
                    <span className="font-semibold text-teal-600">${iv.price}</span>
                  </div>
                ))}
              </div>
            ))}

            {estimate.notes && (
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-700 mb-1">Clinic Notes:</p>
                <p className="text-sm text-blue-800">{estimate.notes}</p>
              </div>
            )}

            <div className="border-t border-slate-200 pt-3 mt-3">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total Estimate</span>
                <span className="text-2xl font-bold text-teal-600" data-testid="patient-total-cost">${estimate.total_cost}</span>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                * Final pricing confirmed after clinical examination
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Response Section */}
        {!responded ? (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Your Response</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                data-testid="patient-response-notes"
                value={responseNotes}
                onChange={(e) => setResponseNotes(e.target.value)}
                placeholder="Any questions or notes? (optional)"
                rows={3}
                className="text-sm"
              />
              <div className="flex gap-2">
                <Button
                  data-testid="accept-estimate-btn"
                  onClick={() => respond('accepted')}
                  disabled={responding}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-1.5" />Accept
                </Button>
                <Button
                  data-testid="request-changes-btn"
                  onClick={() => respond('modification_requested')}
                  disabled={responding}
                  variant="outline"
                  className="flex-1"
                >
                  <MessageCircle className="w-4 h-4 mr-1.5" />Request Changes
                </Button>
                <Button
                  data-testid="decline-estimate-btn"
                  onClick={() => respond('declined')}
                  disabled={responding}
                  variant="outline"
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4 mr-1.5" />Decline
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className={
            estimate.status === 'accepted' ? 'border-green-200 bg-green-50' :
            estimate.status === 'declined' ? 'border-red-200 bg-red-50' :
            'border-amber-200 bg-amber-50'
          }>
            <CardContent className="p-4 text-center">
              {estimate.status === 'accepted' && (
                <div>
                  <CheckCircle className="w-10 h-10 text-green-600 mx-auto mb-2" />
                  <p className="font-semibold text-green-800">Estimate Accepted!</p>
                  <p className="text-sm text-green-600 mt-1">Our team will contact you to schedule your appointment.</p>
                </div>
              )}
              {estimate.status === 'declined' && (
                <div>
                  <XCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
                  <p className="font-semibold text-red-800">Estimate Declined</p>
                  <p className="text-sm text-red-600 mt-1">Thank you for your consideration.</p>
                </div>
              )}
              {estimate.status === 'modification_requested' && (
                <div>
                  <MessageCircle className="w-10 h-10 text-amber-600 mx-auto mb-2" />
                  <p className="font-semibold text-amber-800">Modification Requested</p>
                  <p className="text-sm text-amber-600 mt-1">Our team will review your request and get back to you.</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Contact */}
        <div className="text-center py-4">
          <p className="text-sm text-muted-foreground mb-2">Have questions? Contact us:</p>
          <a
            href="https://wa.me/355686089622"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-full text-sm hover:bg-green-700 transition"
          >
            <Phone className="w-4 h-4" />WhatsApp +355 68 608 9622
          </a>
        </div>
      </main>
    </div>
  );
}
